import React, { useState } from 'react';
import { runMultiAgentAnalysis } from '../../services/multiAgent';

const THEME = {
  colors: {
    bg: '#0a0e1a', card: '#111827', border: '#1e293b',
    text: '#e2e8f0', textMuted: '#94a3b8',
    green: '#00ff88', red: '#ff3333', gold: '#ffd700',
    blue: '#00d4ff', purple: '#a78bfa', orange: '#fb923c',
  },
};

const S = {
  page:    { padding: '16px', color: THEME.colors.text, fontFamily: 'monospace', maxWidth: '100%' },
  title:   { fontSize: '18px', fontWeight: '700', color: THEME.colors.gold, marginBottom: '4px', letterSpacing: '2px' },
  sub:     { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '16px', letterSpacing: '1px' },
  card:    (bg) => ({ background: bg || THEME.colors.card, border: `1px solid ${THEME.colors.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' }),
  section: { fontSize: '10px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '2px', marginBottom: '8px' },
  badge:   (bg, col) => ({ background: bg, color: col || '#000', borderRadius: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', display: 'inline-block' }),
};

const AGENTS = [
  { step: 1, name: 'Market Analyst',  icon: '📐', color: THEME.colors.blue,   desc: 'HTF bias · OB · FVG · Structure' },
  { step: 2, name: 'Risk Manager',    icon: '🛡️', color: THEME.colors.orange, desc: 'Position size · Risk limits' },
  { step: 3, name: 'News Agent',      icon: '📰', color: THEME.colors.purple, desc: 'News risk · Session context' },
  { step: 4, name: 'Trade Manager',   icon: '📊', color: THEME.colors.gold,   desc: 'Open trade management' },
  { step: 5, name: 'Master Decision', icon: '🧠', color: THEME.colors.green,  desc: 'Final verdict · Trade plan' },
];

const verdictColor = (v) => {
  if (!v) return THEME.colors.textMuted;
  if (v === 'BUY')         return THEME.colors.green;
  if (v === 'SELL')        return THEME.colors.red;
  if (v === 'WAIT')        return THEME.colors.gold;
  if (v === 'MANAGE_OPEN') return THEME.colors.blue;
  return THEME.colors.textMuted;
};

const gradeColor = (g) => {
  if (g === 'A+' || g === 'A') return THEME.colors.green;
  if (g === 'B')               return THEME.colors.gold;
  return THEME.colors.red;
};

// ─── Agent Status Card ────────────────────────────────────────────────────────
function AgentCard({ agent, stepState }) {
  const [expanded, setExpanded] = useState(false);
  const status = stepState?.status || 'waiting';

  const statusIcon = status === 'running' ? '⏳' : status === 'done' ? '✅' : status === 'error' ? '❌' : '⬜';
  const statusColor = status === 'running' ? THEME.colors.gold : status === 'done' ? THEME.colors.green : status === 'error' ? THEME.colors.red : THEME.colors.textMuted;

  return (
    <div style={{ ...S.card(status === 'running' ? `rgba(${agent.color === THEME.colors.blue ? '0,212,255' : agent.color === THEME.colors.green ? '0,255,136' : '255,215,0'},0.06)` : undefined), padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: stepState?.result ? 'pointer' : 'default' }}
        onClick={() => stepState?.result && setExpanded(e => !e)}>
        <span style={{ fontSize: '18px' }}>{agent.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: status === 'done' ? THEME.colors.text : THEME.colors.textMuted }}>
            {agent.name}
          </div>
          <div style={{ fontSize: '10px', color: THEME.colors.textMuted }}>{agent.desc}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '16px' }}>{statusIcon}</div>
          <div style={{ fontSize: '10px', color: statusColor }}>{status.toUpperCase()}</div>
        </div>
      </div>

      {/* Quick result preview */}
      {status === 'done' && stepState?.result && !expanded && (
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${THEME.colors.border}`, fontSize: '11px', color: THEME.colors.textMuted }}>
          {agent.step === 1 && stepState.result.htf_bias && `${stepState.result.htf_bias} · ${stepState.result.structure_state || ''} (real math, zero AI)`}
          {agent.step === 2 && stepState.result.risk_verdict && `${stepState.result.risk_verdict} · ${stepState.result.position_size_pct}% size · ${stepState.result.reason?.slice(0, 60)}...`}
          {agent.step === 3 && stepState.result.news_risk && `${stepState.result.news_risk} news risk · ${stepState.result.news_verdict}`}
          {agent.step === 4 && `${stepState.result.overall_portfolio_verdict || 'N/A'} · ${stepState.result.has_open_trades ? 'Has open trades' : 'No open trades'}`}
          {agent.step === 5 && stepState.result.final_verdict && (
            <span style={{ color: verdictColor(stepState.result.final_verdict), fontWeight: '700' }}>
              {stepState.result.final_verdict} — Grade {stepState.result.grade}{stepState.result.timeframe ? ` — ${stepState.result.timeframe}` : ''}
            </span>
          )}
          {stepState?.result && <span style={{ color: THEME.colors.blue, marginLeft: '8px', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>▼ expand</span>}
        </div>
      )}

      {/* Expanded detail */}
      {expanded && stepState?.result && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${THEME.colors.border}` }}>
          {Object.entries(stepState.result).filter(([k]) => !['parse_error', 'raw_response'].includes(k)).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: '8px', marginBottom: '4px', fontSize: '11px' }}>
              <span style={{ color: THEME.colors.textMuted, minWidth: '130px', flexShrink: 0 }}>{k.replace(/_/g, ' ')}:</span>
              <span style={{ color: Array.isArray(v) ? THEME.colors.textMuted : typeof v === 'number' ? THEME.colors.gold : THEME.colors.text, wordBreak: 'break-word' }}>
                {Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
          <span style={{ fontSize: '11px', color: THEME.colors.blue, cursor: 'pointer' }} onClick={() => setExpanded(false)}>▲ collapse</span>
        </div>
      )}

      {status === 'error' && (
        <div style={{ marginTop: '6px', fontSize: '11px', color: THEME.colors.red }}>{stepState?.error}</div>
      )}
    </div>
  );
}

// ─── Final Decision Card ──────────────────────────────────────────────────────
function FinalDecisionCard({ result }) {
  if (!result || result.error) return null;

  return (
    <div style={{ ...S.card(`rgba(${result.final_verdict === 'BUY' ? '0,255,136' : result.final_verdict === 'SELL' ? '255,51,51' : '255,215,0'},0.08)`), padding: '16px' }}>
      <div style={S.section}>⚡ MASTER DECISION</div>

      {/* Verdict + Grade */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '28px', fontWeight: '900', color: verdictColor(result.final_verdict), letterSpacing: '3px' }}>
            {result.final_verdict?.replace('_', ' ')}
          </div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted }}>
            {result.timeframe ? `Timeframe: ${result.timeframe} (real Structure+Fibonacci+ADX)` : 'No fabricated confidence — grade only appears when all real gates pass'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '32px', fontWeight: '900', color: gradeColor(result.grade) }}>{result.grade}</div>
          <div style={{ fontSize: '10px', color: THEME.colors.textMuted }}>GRADE</div>
        </div>
      </div>

      {/* Trade plan */}
      {result.entry_zone && (
        <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '12px' }}>
            {result.entry_zone && <div><span style={{ color: THEME.colors.textMuted }}>Entry: </span><strong style={{ color: THEME.colors.gold }}>{result.entry_zone}</strong></div>}
            {result.stop_loss   && <div><span style={{ color: THEME.colors.textMuted }}>SL: </span><strong style={{ color: THEME.colors.red }}>{result.stop_loss}</strong></div>}
            {result.tp1         && <div><span style={{ color: THEME.colors.textMuted }}>TP1: </span><strong style={{ color: THEME.colors.green }}>{result.tp1}</strong></div>}
            {result.tp2         && <div><span style={{ color: THEME.colors.textMuted }}>TP2: </span><strong style={{ color: THEME.colors.green }}>{result.tp2}</strong></div>}
            {result.lot_size    && <div><span style={{ color: THEME.colors.textMuted }}>Lots: </span><strong>{result.lot_size}</strong></div>}
            {result.risk_reward && <div><span style={{ color: THEME.colors.textMuted }}>R:R: </span><strong style={{ color: THEME.colors.blue }}>{result.risk_reward}</strong></div>}
          </div>
        </div>
      )}

      {/* Reasoning */}
      {result.reasoning && (
        <div style={{ fontSize: '12px', color: THEME.colors.text, lineHeight: '1.6', marginBottom: '10px' }}>
          {result.reasoning}
        </div>
      )}

      {/* Immediate action */}
      {result.immediate_action && (
        <div style={{ padding: '8px 12px', background: 'rgba(0,212,255,0.08)', borderRadius: '6px', fontSize: '12px', color: THEME.colors.blue, marginBottom: '10px' }}>
          ⚡ <strong>NOW:</strong> {result.immediate_action}
        </div>
      )}

      {/* Key risks */}
      {result.key_risks?.length > 0 && (
        <div>
          <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginBottom: '4px' }}>KEY RISKS:</div>
          {result.key_risks.map((r, i) => (
            <div key={i} style={{ fontSize: '11px', color: THEME.colors.orange }}>⚠️ {r}</div>
          ))}
        </div>
      )}

      {/* Agent consensus */}
      {result.consensus_summary && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${THEME.colors.border}`, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {Object.entries(result.consensus_summary).map(([agent, verdict]) => (
            <span key={agent} style={S.badge('rgba(255,255,255,0.06)', THEME.colors.textMuted)}>
              {agent}: <strong style={{ color: THEME.colors.text }}>{verdict}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MultiAgentScreen() {
  const [question, setQuestion] = useState('');
  const [running,  setRunning]  = useState(false);
  const [steps,    setSteps]    = useState({});   // { step: { status, result, error } }
  const [results,  setResults]  = useState(null);

  const handleRun = async () => {
    if (!question.trim() || running) return;
    setRunning(true);
    setSteps({});
    setResults(null);

    try {
      const res = await runMultiAgentAnalysis(question, ({ step, name, status, result, error }) => {
        setSteps(prev => ({ ...prev, [step]: { name, status, result, error } }));
      });
      setResults(res);
    } catch (e) {
      setSteps(prev => ({ ...prev, error: e.message }));
    } finally {
      setRunning(false);
    }
  };

  const quickQuestions = [
    'Should I buy or sell XAU/USD right now?',
    'Is this a good time to enter a trade?',
    'What are the key levels I should watch?',
    'Should I close or hold my open trades?',
  ];

  return (
    <div style={S.page}>
      <div style={S.title}>🤖 MULTI-AGENT SYSTEM</div>
      <div style={S.sub}>5 SPECIALIZED AI AGENTS · MARKET · RISK · NEWS · TRADES · MASTER</div>

      {/* Explanation */}
      <div style={{ ...S.card('rgba(0,212,255,0.05)'), padding: '12px 14px', marginBottom: '14px' }}>
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, lineHeight: '1.7' }}>
          Ask a question → 5 agents analyze in parallel → Master Agent synthesizes the final decision.
          Each agent is a specialized Groq LLM call focused on its domain.
        </div>
      </div>

      {/* Input */}
      <div style={S.card()}>
        <div style={S.section}>ASK ALL 5 AGENTS</div>
        <textarea
          style={{ width: '100%', background: '#0d1117', border: `1px solid ${THEME.colors.border}`, borderRadius: '6px', padding: '10px', color: THEME.colors.text, fontSize: '13px', fontFamily: 'monospace', resize: 'vertical', minHeight: '60px', boxSizing: 'border-box', marginBottom: '10px' }}
          placeholder="e.g. Should I buy XAU/USD now? Is this a good setup?"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleRun())}
        />

        {/* Quick questions */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          {quickQuestions.map(q => (
            <button key={q}
              style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${THEME.colors.border}`, borderRadius: '12px', padding: '4px 10px', fontSize: '11px', color: THEME.colors.textMuted, cursor: 'pointer', fontFamily: 'monospace' }}
              onClick={() => setQuestion(q)}>
              {q}
            </button>
          ))}
        </div>

        <button
          style={{ width: '100%', padding: '12px', background: running ? 'rgba(255,215,0,0.1)' : 'rgba(0,212,255,0.15)', border: `1px solid ${running ? THEME.colors.gold : THEME.colors.blue}`, borderRadius: '8px', color: running ? THEME.colors.gold : THEME.colors.blue, fontSize: '13px', fontWeight: '700', cursor: running ? 'not-allowed' : 'pointer', fontFamily: 'monospace', letterSpacing: '1px' }}
          onClick={handleRun}
          disabled={running || !question.trim()}>
          {running ? '⏳ Agents Analyzing... (5 AI calls)' : '⚡ LAUNCH 5-AGENT ANALYSIS'}
        </button>
      </div>

      {/* Agent status cards */}
      {(Object.keys(steps).length > 0 || running) && (
        <div style={{ marginBottom: '4px' }}>
          <div style={{ ...S.section, marginBottom: '10px' }}>AGENT STATUS</div>
          {AGENTS.map(agent => (
            <AgentCard key={agent.step} agent={agent} stepState={steps[agent.step]} />
          ))}
        </div>
      )}

      {/* Final decision */}
      {results?.master && <FinalDecisionCard result={results.master} />}

      {/* Architecture reference */}
      {!running && Object.keys(steps).length === 0 && (
        <div style={{ ...S.card('rgba(255,215,0,0.04)'), padding: '14px' }}>
          <div style={S.section}>HOW IT WORKS</div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, lineHeight: '1.8' }}>
            {AGENTS.map(a => (
              <div key={a.step}>
                <span style={{ color: a.color }}>{a.icon} Agent {a.step} — {a.name}:</span>{' '}
                <span>{a.desc}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '10px', fontSize: '11px', color: THEME.colors.textMuted, padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
            Requires Groq API key in .env — free at console.groq.com. Each analysis uses ~5 Groq API calls.
          </div>
        </div>
      )}
    </div>
  );
}


